// hooks/useFollowRequestActions.ts — accept/decline a single incoming follow
// request, for inline use on a follow_request notification row (NOT the full
// manage-list fetch — see useFollowRequestsList.ts for that). Mirrors
// useMute.ts's shape: a thin, stateless wrapper over the service call.
//
// Components must NOT import followsService/notificationsService directly.

import { followsService } from '@/core/services/followsService';
import { notificationsService } from '@/core/services/notificationsService';
import { ApiError } from '@/core/lib/apiClient';

/**
 * 'ok'      — resolved successfully.
 * 'handled' — 404: someone else already resolved this request (another tab,
 *             the manage list). Not a failure — there is nothing left to do.
 * 'error'   — a genuine failure (network, 500, rate limit). The caller must
 *             NOT treat this as success — buttons should stay, not vanish.
 */
export type FollowRequestActionResult = 'ok' | 'handled' | 'error';

export interface FollowRequestActionsHook {
  accept: (handle: string) => Promise<FollowRequestActionResult>;
  decline: (handle: string) => Promise<FollowRequestActionResult>;
  /** Re-pull the notifications list from the server. Called after a
   * successful accept/decline — the server has already deleted the
   * follow_request notification row (see backend accept/decline_follow_request),
   * so a refetch here is truthful rather than relying on stale local state. */
  refetchNotifications: () => Promise<void>;
}

async function _resolve(call: () => Promise<boolean>): Promise<FollowRequestActionResult> {
  try {
    const ok = await call();
    return ok ? 'ok' : 'error';
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return 'handled';
    return 'error';
  }
}

export function useFollowRequestActions(): FollowRequestActionsHook {
  return {
    accept: (handle: string) => _resolve(() => followsService.acceptRequest(handle)),
    decline: (handle: string) => _resolve(() => followsService.declineRequest(handle)),
    refetchNotifications: () => notificationsService.list().then(() => undefined),
  };
}
